import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  ActivitiesContentType,
  OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS,
  Subject,
  Years,
} from 'global/constants';
import {
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import type { UpsertActivityOutcome } from '../../../../../../gql/schema-types';
import { modifyOutcomesInCache } from 'approot/activity-builder/logic/apollo/modify-outcomes-in-cache';
import { modifyActivitiesPublishChanges } from 'approot/activity-builder/activity-builder.apollo.utils';
import { BoxPanel } from 'approot/shared/styles/box-panel.style';
import { Heading16 } from 'approot/shared/styles/typography.style';
import Text from 'approot/shared/styles/typography/text';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { updateNoteInCache } from '../../teaching-notes.apollo';
import type { TeachingLearningSequenceFragment } from '../../teaching-notes.graphql.generated';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import QuestionTypesView from '../content-points/question-types-view';
import CerTypesView from '../content-points/cer-types-view';
import PerformanceExpectationRemoveModal from './performance-expectation-remove-modal';
import buildGroupsFromActivityOutcomes from './logic/build-performance-expectation-groups';
import computeNextGroupsAfterSave from './logic/compute-next-groups-after-save';
import groupToOutcomes from './logic/group-to-outcomes';
import PerformanceExpectationsEdit from './performance-expectations-edit';
import { emptyKeyActivityFormState } from './use-performance-expectation-standards';
import useUpsertActivityOutcomes from '../use-upsert-activity-outcomes';
import {
  ActivityOutcomes,
  PerformanceExpectationGroup,
} from '../activity-outcomes.constants';
import sortPerformanceExpectationDimensions from 'approot/shared/outcomes/sort-performance-expectations-dimensions';

type PerformanceExpectationGroupEditProps = {
  subject: Subject;
  years: Years[];
  contentId: number;
  contentType: ActivitiesContentType;
  sectionId?: string | null;
  activityId?: string | null;
  sequenceItem?: TeachingLearningSequenceFragment;
  activityOutcomes?: ActivityOutcomes | null;
  onSaveComplete?: () => void;
  /** When set (e.g. 1 for lesson introduction), caps the number of PE blocks. */
  maxPeBlocks?: number;
};

type PerformanceExpectationBlockViewProps = {
  group: PerformanceExpectationGroup;
  index: number;
  isEditingAny: boolean;
  onRequestDelete: (index: number) => void;
  onConfirmDelete: (index: number) => void;
  onEdit: (index: number) => void;
};

const PerformanceExpectationBlockView = ({
  group,
  index,
  isEditingAny,
  onRequestDelete,
  onConfirmDelete,
  onEdit,
}: PerformanceExpectationBlockViewProps) => {
  const peValues =
    group.outcomes[OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS] ?? [];
  const hasPe = peValues.length > 0;

  return (
    <>
      {hasPe && (
        <Box $marginBottom={12}>
          <Box $justifyContent="space-between" $alignItems="center">
            <Heading16 as="h3" marginBottom={0}>
              Performance Expectation
            </Heading16>
            {isEditingAny ? (
              <TooltipWrapper
                multiline
                hoverText="You'll need to save your current outcome before deleting another."
              >
                <DeleteButtonIcon
                  $isSmall
                  $isDisabled
                  aria-label={`Delete performance expectation: ${peValues[0].label}`}
                  aria-haspopup="dialog"
                  onClick={() => onRequestDelete(index)}
                />
              </TooltipWrapper>
            ) : (
              <DeleteButtonIcon
                $isSmall
                aria-label={`Delete performance expectation: ${peValues[0].label}`}
                aria-haspopup="dialog"
                onClick={() => onConfirmDelete(index)}
              />
            )}
          </Box>
          <Text $marginBottom={12}>{peValues[0].label}</Text>
          <SettingsHr />
        </Box>
      )}
      {sortPerformanceExpectationDimensions(
        Object.keys(group.outcomes).filter(
          k => k !== OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS
        )
      ).map(key => {
        const values = group.outcomes[key] ?? [];
        const categoryLabel = values[0]?.category;
        if (values.length === 0 || !categoryLabel) return null;
        return (
          <Box key={key} $marginBottom={12}>
            <Heading16 as="h3" marginBottom={0}>
              {categoryLabel}
            </Heading16>
            {values.map(selected => (
              <Text key={selected.value} $marginBottom={0}>
                {selected.name ?? selected.label}
              </Text>
            ))}
          </Box>
        );
      })}
      {group.questionTypes.length > 0 && (
        <QuestionTypesView questionTypes={group.questionTypes} />
      )}
      {group.cerTypes.length > 0 && <CerTypesView cerTypes={group.cerTypes} />}
      <Box $justifyContent="space-between" $alignItems="center" $marginTop={16}>
        <SecondaryButton
          type="button"
          $isSmall
          onClick={() => onEdit(index)}
          aria-label="Edit this performance expectation"
        >
          Edit
        </SecondaryButton>
      </Box>
    </>
  );
};

/**
 * Key Activity Information for US: multiple PE blocks (each PE + DCI, SEP, CCC).
 * View mode shows all blocks; Edit and Add Another use KeyActivityInformationEdit.
 */
const PerformanceExpectationGroupEdit = ({
  subject,
  years,
  contentId,
  contentType,
  sectionId,
  activityId,
  activityOutcomes,
  sequenceItem,
  onSaveComplete,
  maxPeBlocks,
}: PerformanceExpectationGroupEditProps) => {
  const client = useApolloClient();

  const [groups, setGroups] = useState<PerformanceExpectationGroup[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAddNew, setIsAddNew] = useState<boolean>(false);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(
    null
  );

  const initialGroups = useMemo(() => {
    if (!activityOutcomes?.outcomes?.length) {
      return null;
    }
    return buildGroupsFromActivityOutcomes(activityOutcomes);
  }, [activityOutcomes]);

  useEffect(() => {
    if (initialGroups != null) {
      setGroups(maxPeBlocks === 1 ? initialGroups.slice(0, 1) : initialGroups);
    }
  }, [initialGroups, maxPeBlocks]);

  const emptyPerformanceExpectationGroup: PerformanceExpectationGroup = useMemo(
    () => ({
      outcomes: emptyKeyActivityFormState(),
      questionTypes: [],
      cerTypes: [],
    }),
    []
  );

  const { showError } = useSnackbar();

  const { upsertActivityOutcomes, loading: saveLoading } =
    useUpsertActivityOutcomes();

  const buildPayloadFromGroups = useCallback(
    (groupsToSave: PerformanceExpectationGroup[]): UpsertActivityOutcome[] =>
      groupsToSave.flatMap(groupToOutcomes),
    []
  );

  const persistGroups = useCallback(
    (groupsToSave: PerformanceExpectationGroup[]) => {
      const payload = buildPayloadFromGroups(groupsToSave);
      upsertActivityOutcomes({
        variables: {
          input: {
            contentId,
            contentType,
            sectionId: sectionId ?? undefined,
            activityId: activityId ?? undefined,
            outcomes: payload,
          },
        },
        // Supplying onError also stops the mutate promise rejecting, so the
        // .then below runs on failure — hence the early return on no data.
        onError: () =>
          showError(
            'Something went wrong saving this Performance Expectation.'
          ),
      }).then(res => {
        const data = res.data?.upsertActivityOutcomes;
        if (!data) return;
        if (sequenceItem && sectionId && activityId) {
          updateNoteInCache({
            client,
            note: {
              ...sequenceItem,
              activityOutcomes: data,
            },
          });
        } else {
          modifyOutcomesInCache({
            client,
            id: contentId,
            outcomes: data.outcomes,
            contentType,
          });
        }
        modifyActivitiesPublishChanges({
          client,
          id: contentId,
          contentType,
        });
        onSaveComplete?.();
      });
    },
    [
      sequenceItem,
      buildPayloadFromGroups,
      upsertActivityOutcomes,
      contentId,
      contentType,
      sectionId,
      activityId,
      client,
      showError,
      onSaveComplete,
    ]
  );

  const handleSaveFromEdit = useCallback(
    (formState: PerformanceExpectationGroup) => {
      const nextGroups = computeNextGroupsAfterSave({
        groups,
        formState,
        isAddNew,
        editingIndex: editingIndex ?? null,
        maxPeBlocks,
      });
      setGroups(nextGroups);
      persistGroups(nextGroups);
      setIsAddNew(false);
      setEditingIndex(null);
    },
    [groups, isAddNew, editingIndex, maxPeBlocks, persistGroups]
  );

  /**
   * Remove a PE group and persist the remaining outcomes.
   */
  const handleDeletePerformanceExpectation = useCallback(
    (index: number) => {
      const nextGroups = groups.filter((_, i) => i !== index);
      setGroups(nextGroups);
      if (editingIndex === index) {
        setEditingIndex(null);
      } else if (editingIndex != null && editingIndex > index) {
        setEditingIndex(editingIndex - 1);
      }
      persistGroups(nextGroups);
    },
    [groups, editingIndex, persistGroups]
  );

  const hasAnyOutcomes = groups.length > 0;

  const isEditingAny = editingIndex !== null || isAddNew;

  /**
   * PE outcome ids to exclude from the dropdown so the same PE cannot be
   * selected in multiple blocks. When editing block at excludeIndex, only
   * other blocks' PEs are excluded. When adding new (excludeIndex null),
   * all existing blocks' PEs are excluded.
   */
  const getExcludePeIds = useCallback(
    (excludeIndex: number | null): number[] => {
      const ids: number[] = [];
      groups.forEach((group, index) => {
        if (excludeIndex !== null && index === excludeIndex) return;
        const peValue =
          group.outcomes[OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS]?.[0]?.value;
        if (peValue != null) ids.push(peValue);
      });
      return ids;
    },
    [groups]
  );

  return (
    <>
      <SettingsGroup as="fieldset" $marginBottom={4}>
        <SettingsLabel as="legend">Key Activity Information</SettingsLabel>

        {groups.map((group, index) => (
          <BoxPanel key={index} $marginBottom={12} $isOverflowVisible>
            {editingIndex === index ? (
              <PerformanceExpectationsEdit
                key={index}
                subject={subject}
                years={years}
                initialGroup={group}
                onSave={handleSaveFromEdit}
                onCancel={() => setEditingIndex(null)}
                isSaving={saveLoading}
                instanceId={index}
                excludePeIds={getExcludePeIds(index)}
              />
            ) : (
              <PerformanceExpectationBlockView
                group={group}
                index={index}
                isEditingAny={isEditingAny}
                onRequestDelete={handleDeletePerformanceExpectation}
                onConfirmDelete={setDeleteConfirmIndex}
                onEdit={setEditingIndex}
              />
            )}
          </BoxPanel>
        ))}

        {isAddNew && (
          <BoxPanel $marginBottom={12} $isOverflowVisible>
            <PerformanceExpectationsEdit
              key="new"
              subject={subject}
              years={years}
              initialGroup={emptyPerformanceExpectationGroup}
              onSave={handleSaveFromEdit}
              onCancel={() => setIsAddNew(false)}
              isSaving={saveLoading}
              instanceId="new"
              excludePeIds={getExcludePeIds(null)}
            />
          </BoxPanel>
        )}
        {!(maxPeBlocks === 1 && groups.length >= 1) && (
          <Box $marginBottom={12}>
            <LinkButton
              type="button"
              style={{ textAlign: 'left' }}
              disabled={isEditingAny}
              onClick={() => setIsAddNew(true)}
            >
              {hasAnyOutcomes
                ? 'Add Another Performance Expectation'
                : 'Add a Performance Expectation'}
            </LinkButton>
          </Box>
        )}
      </SettingsGroup>

      {deleteConfirmIndex != null && (
        <PerformanceExpectationRemoveModal
          isVisible={deleteConfirmIndex !== null}
          isSaving={saveLoading}
          onClose={() => setDeleteConfirmIndex(null)}
          onConfirm={() => {
            if (deleteConfirmIndex != null) {
              handleDeletePerformanceExpectation(deleteConfirmIndex);
              setDeleteConfirmIndex(null);
            }
          }}
        />
      )}
    </>
  );
};

export default PerformanceExpectationGroupEdit;
